/**
 * CUSTOM EXAMPLE PROFILE (spec 85).
 *
 * A worked example of what a measurement-backed component looks like, so that
 * anyone adding their own hardware can see exactly which fields carry the
 * measurement and how provenance is recorded.
 *
 * READ THIS BEFORE COPYING: the numbers below are ILLUSTRATIVE. They describe a
 * device that does not exist, and the `measurement` block says so in the rig
 * field. It is included to demonstrate the schema, not to assert a measurement.
 * Every field marked MEASURED here would, in a real profile, correspond to a
 * file you imported through the Measurement Lab.
 */

import type { AnyProfile, DacProfile } from '../../types/hardware';
import { frCurve } from '../../types/curves';
import { HARMONIC_SHAPES, harmonicsFromThd, img, usd } from '../kit';

const MEASUREMENT = {
  rig: 'SYNTHETIC EXAMPLE DATA - NOT A REAL MEASUREMENT',
  date: '2026-01-15',
  operator: 'DAC ENGINE example generator',
  conditions: 'Illustrative values showing the schema shape. 2 Vrms into 100 kohm, 24/96 input, 1/12 octave smoothing.',
  smoothingOct: 12,
  fileName: 'example-dac-response.csv',
};

/**
 * A measured frequency response is authored exactly like a modelled one -- the
 * only difference is the confidence tag and the measurement block. The DSP
 * compiler cannot tell them apart, which is the whole point of the design.
 */
const MEASURED_RESPONSE = frCurve(
  [
    [10, -0.09], [20, -0.03], [50, -0.01], [100, 0.0], [200, 0.0], [500, 0.0],
    [1000, 0.0], [2000, 0.0], [5000, -0.01], [10000, -0.03], [15000, -0.08],
    [20000, -0.17], [30000, -0.62], [40000, -1.44], [60000, -4.1],
  ],
  'MEASURED',
  'example-dac-response.csv, imported via Measurement Lab',
);

const EXAMPLE_DAC: DacProfile = {
  id: 'dac.example.measured-reference',
  category: 'dac',
  manufacturer: 'EXAMPLE',
  model: 'Measured Reference (template)',
  price: usd(2500, 'APPROXIMATE', 2026, 'Placeholder price; this device is not real.'),
  version: 3,
  revised: '2026-01-15',
  confidence: 'MEASURED',
  measurement: MEASUREMENT,
  provenanceNote:
    'TEMPLATE. Frequency response and THD spectrum tagged MEASURED with an attached measurement block; noise floor DERIVED from the same capture; jitter ESTIMATED because the example rig did not measure it. This mixture is normal and is exactly what a real profile looks like: different fields legitimately have different confidence.',
  notes:
    'Copy this file to create your own hardware. The three things that make a profile measurement-backed rather than modelled are: (1) confidence MEASURED on the specific curves you actually captured, (2) a measurement block naming the rig, date and conditions, and (3) a provenanceNote that is honest about which fields were NOT measured. Leave anything you did not measure at ESTIMATED or MODELLED -- the confidence indicator aggregates every tag in the profile, so overstating one field degrades the credibility of the whole thing.',
  tags: ['example', 'template', 'measurement-backed'],
  year: 2026,
  countryOfOrigin: 'N/A',
  image: img('dac.example.measured-reference', '#3ddc97'),
  isExample: true,

  conversion: {
    architecture: 'delta-sigma',
    core: 'Example converter core',
    effectiveBits: 21,
    internalOversampling: 8,
    noiseShaperOrder: 5,
    jitterPsRms: 40,
    jitterKind: 'random',
    confidence: 'ESTIMATED',
  },
  filters: [
    {
      id: 'measured-lp',
      label: 'Sharp linear phase (as measured)',
      mode: 'linear-phase',
      cutoffRatio: 0.91,
      transitionRatio: 0.085,
      stopbandDb: -118,
      taps: 128,
      confidence: 'MEASURED',
    },
  ],
  defaultFilterId: 'measured-lp',
  analog: {
    stage: 'op-amp',
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 47,
    balancedAvailable: true,
    response: MEASURED_RESPONSE,
    phase: { kind: 'phase', derive: 'minimum-phase', confidence: 'DERIVED' },
    confidence: 'MEASURED',
  },
  nonlinear: {
    kind: 'sigma-delta',
    headroomDb: 14,
    amount: 0.3,
    bias: 0,
    asymmetry: 0.06,
    oversample: 4,
    confidence: 'MODELLED',
  },
  harmonics: {
    ...harmonicsFromThd(0.00018, HARMONIC_SHAPES.deltaSigma, -6, 'MEASURED', 'THD spectrum from example-dac-thd.csv'),
    measurement: MEASUREMENT,
  },
  noise: {
    kind: 'noise',
    levelDbFS: -122,
    shape: 'shaped',
    shapingSlopeDbOct: 8,
    shapeKneeHz: 22000,
    channelCorrelation: 0.04,
    confidence: 'DERIVED',
    source: 'Integrated from the measured noise spectrum, 20 Hz - 20 kHz unweighted',
  },
  channels: {
    imbalanceDb: 0.02,
    crosstalkDb: -132,
    crosstalkSlopeDbDec: 20,
    skewSamples: 0,
  },
  sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
  maxBitDepth: 32,
  dsdSupport: 'dsd256',
  dynamicRangeDb: 122,
  thdPlusNDb: -115,
  time: { preRing: 1, postRing: 1 },
};

export const CUSTOM_EXAMPLES: AnyProfile[] = [EXAMPLE_DAC];
export { EXAMPLE_DAC };
